/** Adapter between the API project (raw Prisma) and the UI `Project`. */

import type {
  Project,
  ProjectCommentEntry,
  ProjectStatus,
  ProjectTaskRef,
  TaskStatus,
} from "@/types";

export interface ApiProjectComment {
  id: string;
  createdAt: string;
  body: string;
  author?: { id: string; name: string } | null;
}

export interface ApiProjectTask {
  id: string;
  title: string;
  status: TaskStatus;
  progress: number;
  dueDate?: string | null;
}

/** Flattened MemberProject → TeamMember ref (spec §1 « membres associés »). */
export interface ApiProjectMember {
  id: string;
  name: string;
  role?: string | null;
  status?: string | null;
}

/** AuditLog entry of the project (spec §1 « activité récente »). */
export interface ApiProjectActivity {
  id: string;
  createdAt: string;
  action: string;
  entityType?: string | null;
  entityLabel?: string | null;
  actor?: { id: string; name: string } | null;
}

export interface ApiProject {
  id: string;
  name: string;
  clientId: string;
  serviceId?: string | null;
  status: ProjectStatus;
  budget?: number | string | null;
  startDate?: string | null;
  expectedDelivery?: string | null;
  description?: string | null;
  client?: { id: string; name: string } | null;
  service?: { id: string; name: string } | null;
  leadOwner?: { id: string; name: string } | null;
  comments?: ApiProjectComment[];
  tasks?: ApiProjectTask[];
  members?: ApiProjectMember[];
  recentActivity?: ApiProjectActivity[];
}

/**
 * Spec §1 — la page Projets est purement informative/opérationnelle : aucune
 * donnée financière n'est saisie ni envoyée (le budget reste géré en Finance).
 */
export interface ProjectInput {
  name: string;
  clientId: string;
  serviceId?: string;
  status?: ProjectStatus;
  startDate?: string;
  expectedDelivery?: string;
  leadOwnerId?: string;
  description?: string;
}

function toNumberOrUndefined(value: number | string | null | undefined): number | undefined {
  if (value === null || value === undefined) return undefined;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : undefined;
}

function formatDate(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

/** Journal entries need the time as well as the day. */
function formatDateTime(raw: string | null | undefined): string {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("fr-FR", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function toProject(api: ApiProject): Project {
  const comments: ProjectCommentEntry[] = (api.comments ?? []).map((c) => ({
    id: c.id,
    authorName: c.author?.name ?? "—",
    date: formatDate(c.createdAt) ?? "—",
    body: c.body,
  }));
  const tasks: ProjectTaskRef[] = (api.tasks ?? []).map((t) => ({
    id: t.id,
    title: t.title,
    status: t.status,
    progress: t.progress,
    dueDate: formatDate(t.dueDate),
  }));
  const members = (api.members ?? []).map((m) => ({
    id: m.id,
    name: m.name,
    role: m.role ?? undefined,
  }));
  // spec §3 — % avancement auto from the linked tasks
  const tasksDone = tasks.filter((t) => t.status === "DONE").length;
  const progress = tasks.length > 0 ? Math.round((100 * tasksDone) / tasks.length) : 0;
  return {
    id: api.id,
    name: api.name,
    clientId: api.clientId,
    clientName: api.client?.name ?? "—",
    serviceId: api.serviceId ?? undefined,
    serviceName: api.service?.name,
    status: api.status,
    budget: toNumberOrUndefined(api.budget),
    startDate: formatDate(api.startDate),
    expectedDelivery: formatDate(api.expectedDelivery),
    startDateISO: api.startDate ?? undefined,
    expectedDeliveryISO: api.expectedDelivery ?? undefined,
    leadOwnerId: api.leadOwner?.id,
    leadOwnerName: api.leadOwner?.name,
    description: api.description ?? undefined,
    comments,
    tasks,
    members,
    ...(api.recentActivity
      ? {
          recentActivity: api.recentActivity.map((a) => ({
            id: a.id,
            date: formatDateTime(a.createdAt),
            action: a.action,
            actorName: a.actor?.name ?? "Système",
          })),
        }
      : {}),
    progress,
    tasksDone,
  };
}

export function toProjectWriteBody(input: ProjectInput): Record<string, unknown> {
  return {
    name: input.name,
    clientId: input.clientId,
    ...(input.serviceId ? { serviceId: input.serviceId } : {}),
    ...(input.status ? { status: input.status } : {}),
    ...(input.startDate ? { startDate: input.startDate } : {}),
    ...(input.expectedDelivery ? { expectedDelivery: input.expectedDelivery } : {}),
    ...(input.leadOwnerId ? { leadOwnerId: input.leadOwnerId } : {}),
    ...(input.description ? { description: input.description } : {}),
  };
}
